<script lang="ts">
  import { useLocation, useNavigate } from 'svelte-navigator';
  import { onDestroy, onMount } from 'svelte';
  import Page from '../components/misc/Page.svelte';
  import { AppState } from '../etc/stores';
  import { _ } from 'svelte-i18n';
  import Analytics from '../services/analytics';
  import HeaderInternal from '../components/misc/HeaderInternal.svelte';
  import Heading from '../components/typography/Heading.svelte';
  import AvailableBalance from '../components/misc/AvailableBalance.svelte';
  import { formatMoney, setWidgetConfiguration, sleep } from '../etc/utils';
  import SocialRewardItem from '../components/misc/SocialRewardItem.svelte';
  import FooterWidget from '../components/misc/FooterWidget.svelte';
  import {
    IBusinessSocialReward,
    SocialRewards
  } from '../services/social-rewards';
  import { Container } from '../services/container';
  import { Backend } from '../services/backend';
  import { Customer } from '../services/customer';
  import Spinner from '../components/misc/Spinner.svelte';
  import { Auth } from '../services/auth';
  import { useFinishSignIn } from '../etc/hooks/useModalInternal';
  import { fade, fly } from 'svelte/transition';

  const backend = Container.get(Backend);
  const customer = Container.get(Customer);
  const socialRewards = Container.get(SocialRewards) as SocialRewards;
  const auth = Container.get(Auth);

  let loading = false;

  const location = useLocation();
  const navigate = useNavigate();

  const { open, close } = useFinishSignIn();

  let getAmount = () => '';

  let handleNavigationBackPage;
  let timeoutAnimation;
  let startAnimation = false;

  const navAnimation: boolean = $location.state?.navAnimation;

  $: amount = ['number', 'string'].includes(
    typeof $AppState.customer.wallet?.coins
  )
    ? $AppState.customer.wallet?.coins
    : null;

  $: {
    getAmount = () => {
      if (amount) {
        return formatMoney(
          $AppState.business.location,
          Number(amount).toFixed(2),
          true
        );
      }

      return '...';
    };
  }

  let socialList = [];
  $: {
    socialList = $AppState.socialReward;
  }

  const t = (text: string) => {
    return $_(`social_rewards.${text}`);
  };

  const onBack = () => {
    if (navAnimation) {
      clearTimeout(timeoutAnimation);
      startAnimation = true;
      timeoutAnimation = setTimeout(() => {
        if (handleNavigationBackPage) handleNavigationBackPage();
        navigate(-1);
      }, 600);
    } else {
      navigate(-1);
    }
  };

  const onChangeSocialRewardList = (list: IBusinessSocialReward[]) => {
    AppState.update(state => ({
      ...state,
      socialReward: list
    }));
  };

  const onChangeItemSocialReward = (
    item: IBusinessSocialReward,
    obj: Partial<IBusinessSocialReward>
  ) => {
    const socialRewards = socialList;
    const index = socialRewards.findIndex(element => element.id === item.id);
    socialRewards[index] = {
      ...item,
      ...obj
    };
    onChangeSocialRewardList(socialRewards);
  };

  const onCompleteActive = async (item: IBusinessSocialReward) => {
    try {
      await backend.claimSocialRewards($AppState.business.id, item.type);
      await customer.loadState();
    } catch (error) {}
    Analytics.logEvent(`click-${item.type}`, 'social-rewards-state');
    onChangeItemSocialReward(item, {
      loading: false,
      redeemed: '1'
    });
  };

  const onClick = async (item: IBusinessSocialReward, shareUrl?: string) => {
    const { url } = item.metadata;

    const height = window.innerHeight * 0.8;
    const width = window.innerWidth * 0.8;
    const left = screen.width / 2 - width / 2;
    const top = screen.height / 2 - height / 2;

    onChangeItemSocialReward(item, { loading: true });

    window.open(
      shareUrl || url,
      '_blank',
      `height=${height},width=${width},top=${top},left=${left}`
    );

    await sleep(6000);
    await onCompleteActive(item);
  };

  const loadSocialRewards = async () => {
    try {
      loading = true;
      await socialRewards.loadState();
      await loadClaimedRewards();
    } finally {
      loading = false;
    }
  };

  const loadClaimedRewards = async () => {
    const response = await backend.getClaimedSocialRewards(
      $AppState.business.id
    );

    socialList = socialList.map((item, i) => ({ ...item, ...response[i] }));
  };

  onDestroy(() => {
    setWidgetConfiguration(true, false);
    close();
    clearTimeout(timeoutAnimation);
  });

  onMount(async () => {
    Analytics.logPageView('Social rewards', '/social-rewards');
    setWidgetConfiguration(false, false);
    if (await auth.isSoftLogin()) {
      open();
    } else {
      loadSocialRewards();
    }
  });
</script>

<Page padding="0" hideCloseButton>
  <HeaderInternal
    title={t('title')}
    {onBack}
    {navAnimation}
    {startAnimation}
    bind:handleNavigationBackPage
  />
  <div class="social-rewards">
    <div
      class="social-rewards__header"
      class:--animation={startAnimation}
      in:fly={{
        duration: navAnimation ? 400 : 0,
        y: -120,
        delay: 300,
        opacity: 1
      }}
    >
      <AvailableBalance amount={getAmount()} />
    </div>
    <div
      class="social-rewards__content"
      class:--animation={startAnimation}
      in:fade={{ duration: navAnimation ? 200 : 0 }}
    >
      <Heading class="social-rewards__title" type="h3">
        {t('ways')}
      </Heading>
      {#if loading}
        <div class="social-rewards__loading">
          <Spinner color="#6B6B6B" />
        </div>
      {:else}
        {#each socialList as social}
          <SocialRewardItem
            item={social}
            onClick={shareUrl => onClick(social, shareUrl)}
          />
        {/each}
      {/if}
    </div>
  </div>
  <div
    class="social-rewards__footer"
    class:--animation={startAnimation}
    in:fade={{ duration: navAnimation ? 200 : 0 }}
  >
    <FooterWidget />
  </div>
</Page>

<style lang="scss">
  :global(.social-rewards__title) {
    padding: 24px 0 8px !important;
    font-weight: 700 !important;
    line-height: 24px !important;
  }

  .social-rewards {
    width: 100%;
    height: 100%;
    margin-bottom: 10px;
    isolation: isolate;

    &__loading {
      display: flex;
      justify-content: center;
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px 0;

      background: var(--twism-theme-background);
      border-top: 1px solid var(--twism-theme-shadowColor);
      border-radius: 0px 0px 12px 12px;
      z-index: -1;
      position: relative;
      transition: transform 0.4s ease-in-out;

      &.--animation {
        transform: translateY(-120px);
      }
    }

    &__footer {
      width: 100%;
      transition: opacity 0.3s ease-in-out;
      &.--animation {
        opacity: 0;
      }
    }

    &__content {
      display: flex;
      flex-direction: column;
      flex: 1;
      padding: 0 24px 64px;
      overflow-y: auto;
      overflow-x: hidden;
      overscroll-behavior: contain;
      transition: opacity 0.3s ease-in-out;
      &.--animation {
        opacity: 0;
      }

      &::-webkit-scrollbar {
        display: none;
      }

      @media screen and (max-width: 468px) {
        max-height: 76%;
      }
    }
  }
</style>
